/*
 * Copyright (c) 2024, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AuthContextType,
  User,
  LoginCredentials,
  RegisterCredentials,
} from "../types/auth";
import { AUTH_URLS, getAuthConfig, AuthConfig } from "../config/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Utility function for exponential backoff
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Debounce function with proper type handling

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    // Prevent concurrent auth checks
    if (isChecking) {
      console.log("[AuthProvider] Auth check already in progress, skipping");
      return;
    }

    console.log("[AuthProvider] Checking auth status");
    setIsChecking(true);
    
    try {
      const accessToken = localStorage.getItem("access_token");
      const currentAuthType = localStorage.getItem("auth_type") as
        | "oidc"
        | "builtin"
        | null;

      if (!accessToken || !currentAuthType) {
        console.log("[AuthProvider] No access token or auth type found");
        clearAuth();
        return;
      }

      // Skip check if we're already authenticated with matching token
      if (isAuthenticated && user && accessToken === localStorage.getItem("access_token")) {
        console.log("[AuthProvider] Already authenticated with same token, skipping check");
        return;
      }

      console.log(
        "[AuthProvider] Verifying token for auth type:",
        currentAuthType
      );

      // First verify the token
      const verifyUrl =
        currentAuthType === "oidc"
          ? AUTH_URLS.oidc.verify
          : AUTH_URLS.builtin.verify;

      console.log("[AuthProvider] Verifying token at:", verifyUrl);

      const verifyResponse = await fetch(verifyUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      });

      if (!verifyResponse.ok) {
        if (verifyResponse.status === 429) {
          // Rate limit hit - implement exponential backoff
          const retryAfter = verifyResponse.headers.get("Retry-After");
          const waitTime = retryAfter
            ? parseInt(retryAfter) * 1000
            : Math.min(1000 * Math.pow(2, retryCount), 30000);
          console.log(
            `[AuthProvider] Rate limited, waiting ${waitTime}ms before retry`
          );
          await wait(waitTime);
          setRetryCount((prev) => prev + 1);
          return checkAuthStatus(); // Retry after waiting
        }
        console.error(
          "[AuthProvider] Token verification failed:",
          verifyResponse.status
        );
        clearAuth();
        return;
      }

      // Reset retry count on successful verification
      setRetryCount(0);
      console.log("[AuthProvider] Token verified successfully");

      // Then get user info using the appropriate endpoint
      const isOIDC = currentAuthType === "oidc";
      const userInfoUrl = isOIDC ? AUTH_URLS.oidc.userInfo : AUTH_URLS.userInfo;

      console.log("[AuthProvider] Fetching user info from:", userInfoUrl);

      // Only make one userinfo request
      const userInfoResponse = await fetch(userInfoUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      });

      if (!userInfoResponse.ok) {
        if (userInfoResponse.status === 429) {
          // Rate limit hit - implement exponential backoff
          const retryAfter = userInfoResponse.headers.get("Retry-After");
          const waitTime = retryAfter
            ? parseInt(retryAfter) * 1000
            : Math.min(1000 * Math.pow(2, retryCount), 30000);
          console.log(
            `[AuthProvider] Rate limited, waiting ${waitTime}ms before retry`
          );
          await wait(waitTime);
          setRetryCount((prev) => prev + 1);
          return checkAuthStatus(); // Retry after waiting
        }
        console.error(
          "[AuthProvider] Failed to get user info:",
          userInfoResponse.status
        );
        clearAuth();
        return;
      }

      // Reset retry count on successful user info fetch
      setRetryCount(0);
      const userData = await userInfoResponse.json();
      console.log("[AuthProvider] User info received:", {
        ...userData,
        auth_type: currentAuthType,
      });

      // Only set authenticated after BOTH verify and userinfo succeed
      setUser({ ...userData, auth_type: currentAuthType });
      setIsAuthenticated(true);
      console.log("[AuthProvider] Authentication successful");
    } catch (error) {
      console.error("[AuthProvider] Auth check failed:", error);
      clearAuth();
    } finally {
      setIsChecking(false);
      setLoading(false);
    }
  }, [retryCount, isAuthenticated, user, isChecking]);

  const clearAuth = useCallback(() => {
    console.log("[AuthProvider] Clearing authentication state");
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("auth_type");
    setUser(null);
    setIsAuthenticated(false);
    setRetryCount(0);
  }, []);

  useEffect(() => {
    let mounted = true;
    console.log("[AuthProvider] Initializing auth provider");

    // Fetch auth configuration
    getAuthConfig().then((config) => {
      if (mounted) {
        console.log("[AuthProvider] Received auth config:", config);
        setAuthConfig(config);
      }
    });

    // Check for OIDC auth tokens in URL (after callback)
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access_token");
    const idToken = params.get("id_token");

    if (accessToken && idToken) {
      console.log("[AuthProvider] Found OIDC tokens in URL");
      // Store tokens and remove them from URL
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("id_token", idToken);
      localStorage.setItem("auth_type", "oidc");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Only do one initial auth check
    const storedAccessToken = localStorage.getItem("access_token");
    const storedAuthType = localStorage.getItem("auth_type");

    if (storedAccessToken && storedAuthType && !isAuthenticated) {
      checkAuthStatus();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, []); // Empty deps array since we only want this to run once on mount

  const loginWithOIDC = () => {
    console.log("[AuthProvider] Initiating OIDC login");
    if (!authConfig?.methods.oidc) {
      throw new Error("OIDC authentication is not configured");
    }
    clearAuth();
    window.location.href = AUTH_URLS.oidc.login;
  };

  const login = async (credentials?: LoginCredentials) => {
    console.log(
      "[AuthProvider] Login attempt",
      credentials ? "with credentials" : "with OIDC"
    );
    if (!credentials) {
      return loginWithOIDC();
    }

    try {
      const response = await fetch(AUTH_URLS.builtin.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("[AuthProvider] Login failed:", error);
        throw new Error(error.message || "Login failed");
      }

      const data = await response.json();
      console.log("[AuthProvider] Login successful");
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("auth_type", "builtin");
      await checkAuthStatus();
    } catch (error) {
      console.error("[AuthProvider] Login error:", error);
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    console.log("[AuthProvider] Registration attempt");
    try {
      const response = await fetch(AUTH_URLS.builtin.register, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("[AuthProvider] Registration failed:", error);
        throw new Error(error.message || "Registration failed");
      }

      console.log(
        "[AuthProvider] Registration successful, proceeding to login"
      );
      // After successful registration, log in with the same credentials
      await login({
        username: credentials.username,
        password: credentials.password,
      });
    } catch (error) {
      console.error("[AuthProvider] Registration error:", error);
      throw error;
    }
  };

  const logout = async () => {
    console.log("[AuthProvider] Initiating logout");
    try {
      const currentAuthType = localStorage.getItem("auth_type") as
        | "oidc"
        | "builtin";
      const logoutUrl =
        currentAuthType === "oidc"
          ? AUTH_URLS.oidc.logout
          : AUTH_URLS.builtin.logout;
      const accessToken = localStorage.getItem("access_token");

      console.log(
        "[AuthProvider] Logging out with auth type:",
        currentAuthType
      );

      const response = await fetch(logoutUrl, {
        method: "POST",
        headers: accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : undefined,
        credentials: "include",
      });

      if (!response.ok) {
        console.error("[AuthProvider] Logout request failed:", response.status);
        throw new Error("Logout failed");
      }

      console.log("[AuthProvider] Logout successful");
      clearAuth();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("[AuthProvider] Logout error:", error);
      clearAuth();
      navigate("/login", { replace: true });
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    loginWithOIDC,
    register,
    logout,
    loading,
    authConfig,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
